/**
 * Form templates — Step 43.
 *
 * The reference's "Pre-fill new ticket forms for repetitive tickets". The
 * incident-side equivalent of a catalogue item: somebody has already decided
 * what this KIND of ticket looks like, for the ones an agent raises rather
 * than the ones a requester submits.
 *
 * Draft-first, like every other admin editor here — a template is a form, not
 * a property panel, so nothing commits until Save.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { EmailChipInput } from '../../features/tickets/EmailChipInput';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { ASSIGNEE_ACTOR, ticketsApi } from '../../features/tickets/api';
import { RequesterPicker } from '../../features/tickets/RequesterPicker';
import { TICKET_SOURCES, URGENCY_LEVELS, IMPACT_LEVELS, TEMPLATE_TYPES, TYPE_LABEL } from '@itsm/shared';
import { useConfirm } from '../../lib/confirm';

/** Every field explicitly `| undefined`: `exactOptionalPropertyTypes` is on,
 *  and "don't set this" is a value the editor writes rather than a key it
 *  omits — clearing a field has to be expressible. */
interface Prefill {
  subject?: string | undefined;
  type?: string | undefined;
  source?: string | undefined;
  urgency?: string | undefined;
  impact?: string | undefined;
  priority?: string | undefined;
  groupId?: string | null | undefined;
  assigneeId?: string | null | undefined;
  statusKey?: string | undefined;
  requesterEmail?: string | undefined;
  tags?: string[] | undefined;
  bodyHtml?: string | undefined;
  noteHtml?: string | undefined;
  cc?: string[] | undefined;
}
interface Template {
  id: string;
  name: string;
  description: string | null;
  prefill: Prefill;
  isActive: number;
  sortOrder: number;
  useCount: number;
  lastUsedAt: number | null;
}
type Draft = { id: string; name: string; description: string; prefill: Prefill };

export function TicketTemplatesPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  // Every view has a URL — which template is open is part of where you are.
  const [editId, setEditId] = useUrlState('template', null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: qk.adminTicketTemplates,
    queryFn: () => apiFetch<{ entries: Template[] }>('/api/admin/ticket-templates'),
  });
  const groups = useQuery({ queryKey: qk.orgGroups, queryFn: ticketsApi.groups, staleTime: 5 * 60_000 });
  const statuses = useQuery({
    queryKey: qk.ticketStatuses,
    queryFn: () => ticketsApi.statuses(),
    staleTime: 5 * 60_000,
  });
  const people = useQuery({ queryKey: qk.people, queryFn: ticketsApi.people, staleTime: 5 * 60_000 });

  const server = draft?.id ? list.data?.entries.find((t) => t.id === draft.id) : undefined;
  const dirty =
    !!draft &&
    (!draft.id
      ? !!draft.name.trim()
      : !server ||
        draft.name !== server.name ||
        (draft.description || '') !== (server.description ?? '') ||
        JSON.stringify(draft.prefill) !== JSON.stringify(server.prefill));
  const clearDirty = useDirty(dirty);

  const close = () => {
    // Clear the guard BEFORE navigating: `template` lives in the URL, so
    // closing the editor IS a navigation and would otherwise trip the
    // unsaved-changes blocker on Save and on Cancel alike.
    clearDirty();
    setDraft(null);
    setEditId(null);
    setError(null);
  };

  const open = (t?: Template) => {
    setDraft(
      t
        ? { id: t.id, name: t.name, description: t.description ?? '', prefill: { ...t.prefill } }
        : { id: '', name: '', description: '', prefill: { type: 'incident' } },
    );
    setEditId(t?.id ?? 'new');
    setError(null);
  };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: draft!.name,
        description: draft!.description || undefined,
        prefill: draft!.prefill,
      };
      return draft!.id
        ? apiSend(`/api/admin/ticket-templates/${draft!.id}`, 'PATCH', body)
        : apiSend<{ id: string }>('/api/admin/ticket-templates', 'POST', body);
    },
    onSuccess: (r) => {
      if (!r.ok) {
        /**
         * A 404 here means the template was deleted while it was open — by a
         * colleague, or by whoever was tidying the database. "Not found" on
         * its own is a dead end: the work is still on screen and there is no
         * obvious way to keep it.
         *
         * So say what happened AND what to do, and turn the draft into a new
         * one so Save works on the next press rather than failing forever.
         */
        if (/not found/i.test(r.error ?? '')) {
          setDraft((d) => (d ? { ...d, id: '' } : d));
          setEditId('new');
          setError('This template was deleted while you had it open. Press Save to recreate it.');
          return;
        }
        return setError(r.error ?? 'Could not save');
      }
      close();
      void qc.invalidateQueries({ queryKey: qk.adminTicketTemplates });
      void qc.invalidateQueries({ queryKey: qk.ticketTemplates });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiSend(`/api/admin/ticket-templates/${id}`, 'DELETE'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminTicketTemplates });
      void qc.invalidateQueries({ queryKey: qk.ticketTemplates });
    },
  });

  /** Agents in the template's chosen group — assignment happens within one,
   *  and the API refuses a stranded assignee. */
  const templateGroupMembers =
    (groups.data?.entries ?? []).find((g) => g.id === draft?.prefill.groupId)?.memberIds ?? [];
  const templateAgents = (people.data?.entries ?? []).filter((p) => templateGroupMembers.includes(p.id));

  const setPrefill = (patch: Partial<Prefill>) =>
    setDraft((d) => (d ? { ...d, prefill: { ...d.prefill, ...patch } } : d));

  if (list.isPending)
    return (
      <div className="max-w-4xl space-y-3 p-6">
        <Skeleton className="h-6 w-44" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (list.isError)
    return (
      <Card className="m-6 max-w-4xl">
        <CardContent className="flex flex-col items-start gap-3 p-6">
          <p className="text-sm">Templates could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void list.refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );

  const entries = list.data?.entries ?? [];

  return (
    // `max-w-4xl p-6` like every other admin page. Without it this one ran the
    // full width of a 27-inch screen, so a two-line empty state sat in the
    // middle of an acre of dashed border.
    <div className="max-w-4xl space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <ClipboardList className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Form templates</h1>
        <div className="flex-1" />
        {!editId && (
          <Button onClick={() => open()}>
            <Plus /> New template
          </Button>
        )}
      </div>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Prefill the agent's new-ticket form for the tickets you raise over and over — a monitoring incident, a
        walk-up, a recurring check. Everything a template sets stays editable; it is a starting point, not a
        lock.
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {editId && draft ? (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Template name</label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Monitoring incident"
                  autoFocus
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Description <span className="font-normal">— shown under the picker</span>
                </label>
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="For alerts raised by monitoring"
                />
              </div>
            </div>

            <div className="rounded-md border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                What it fills in
              </p>
              {/* Every field optional and nothing defaulted — a template that
                  silently sets a priority nobody chose is worse than one that
                  sets nothing, because the agent never sees the decision. */}
              <div className="space-y-3">
                {/* Requester first, and Cc with it — the same order as the
                    create form and as the reference's own ticket form, which
                    reads "Search a requester / Add cc / Subject". Who is
                    asking shapes everything under it. */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Requester</label>
                    <Select
                      value={
                        draft.prefill.requesterEmail === undefined
                          ? ''
                          : draft.prefill.requesterEmail === ASSIGNEE_ACTOR
                            ? ASSIGNEE_ACTOR
                            : 'specific'
                      }
                      onChange={(e) =>
                        setPrefill({
                          requesterEmail:
                            e.target.value === ''
                              ? undefined
                              : e.target.value === ASSIGNEE_ACTOR
                                ? ASSIGNEE_ACTOR
                                : '',
                        })
                      }
                    >
                      <option value="">Don't set — the agent picks</option>
                      {/* The sentinel again: a monitoring incident or a
                          backlog task is raised BY the agent, so they are the
                          requester too. Storing the template author's own
                          address would put their name on everybody's tickets. */}
                      <option value={ASSIGNEE_ACTOR}>Whoever creates the ticket</option>
                      <option value="specific">A specific person…</option>
                    </Select>
                  </div>
                  {draft.prefill.requesterEmail !== undefined &&
                    draft.prefill.requesterEmail !== ASSIGNEE_ACTOR && (
                      <div>
                        <label className="mb-1 block text-xs text-muted-foreground">Which person</label>
                        {/* The SAME searchable picker the create form uses.
                            Kai: "the requester should be dropdown selectable
                            on both" — a typed address is a typo waiting to
                            create a duplicate person, and the two forms
                            disagreeing about how you pick a requester is the
                            kind of small inconsistency that makes a product
                            feel unfinished. */}
                        <RequesterPicker
                          value={{ email: draft.prefill.requesterEmail, name: '', isNew: false }}
                          onChange={(v) => setPrefill({ requesterEmail: v.email })}
                        />
                      </div>
                    )}
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Cc</label>
                  <EmailChipInput
                    value={draft.prefill.cc ?? []}
                    onChange={(v) => setPrefill({ cc: v.length ? v : undefined })}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Subject</label>
                  <Input
                    value={draft.prefill.subject ?? ''}
                    onChange={(e) => setPrefill({ subject: e.target.value || undefined })}
                    placeholder="Leave blank to let the agent write it"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Status</label>
                    <Select
                      value={draft.prefill.statusKey ?? ''}
                      onChange={(e) => setPrefill({ statusKey: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {(statuses.data?.entries ?? []).map((st) => (
                        <option key={st.key} value={st.key}>
                          {st.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Priority</label>
                    <Select
                      value={draft.prefill.priority ?? ''}
                      onChange={(e) => setPrefill({ priority: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {['low', 'medium', 'high', 'urgent'].map((pr) => (
                        <option key={pr} value={pr}>
                          {pr[0]!.toUpperCase() + pr.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Type</label>
                    <Select
                      value={draft.prefill.type ?? ''}
                      onChange={(e) => setPrefill({ type: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {/* Step 199 — every type, not two. A change carries a
                          justification, a test plan and a backout plan that are
                          near-identical every time you patch the same class of
                          kit, which makes it the type MOST worth prefilling. */}
                      {TEMPLATE_TYPES.map((tt) => (
                        <option key={tt} value={tt}>
                          {TYPE_LABEL[tt] ?? tt}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Source</label>
                    <Select
                      value={draft.prefill.source ?? ''}
                      onChange={(e) => setPrefill({ source: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {TICKET_SOURCES.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Urgency</label>
                    <Select
                      value={draft.prefill.urgency ?? ''}
                      onChange={(e) => setPrefill({ urgency: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {URGENCY_LEVELS.map((u) => (
                        <option key={u} value={u}>
                          {u[0]!.toUpperCase() + u.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">Impact</label>
                    <Select
                      value={draft.prefill.impact ?? ''}
                      onChange={(e) => setPrefill({ impact: e.target.value || undefined })}
                    >
                      <option value="">Don't set</option>
                      {IMPACT_LEVELS.map((i) => (
                        <option key={i} value={i}>
                          {i[0]!.toUpperCase() + i.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Group</label>
                  <Select
                    value={draft.prefill.groupId ?? ''}
                    onChange={(e) =>
                      // Clearing the agent with the group: an agent who is not
                      // in the chosen group is refused at create, and a
                      // template that cannot be used is worse than one that
                      // sets less.
                      setPrefill({ groupId: e.target.value || undefined, assigneeId: undefined })
                    }
                    className="max-w-sm"
                  >
                    <option value="">Don't set</option>
                    {(groups.data?.entries ?? []).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Agent</label>
                  <Select
                    value={draft.prefill.assigneeId ?? ''}
                    onChange={(e) => setPrefill({ assigneeId: e.target.value || undefined })}
                    className="max-w-sm"
                    disabled={!draft.prefill.groupId}
                  >
                    <option value="">{draft.prefill.groupId ? "Don't set" : 'Pick a group first'}</option>
                    {/* The sentinel, not a name: storing the author's own id
                        would assign every future ticket to whoever happened
                        to write the template. */}
                    <option value={ASSIGNEE_ACTOR}>Whoever creates the ticket</option>
                    {templateAgents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name ?? a.email}
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Assignment happens within a group, so the agent list follows the group above.
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Tags <span className="font-normal">— comma separated</span>
                  </label>
                  <Input
                    value={(draft.prefill.tags ?? []).join(', ')}
                    onChange={(e) => {
                      const v = e.target.value
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean);
                      setPrefill({ tags: v.length ? v : undefined });
                    }}
                    placeholder="broadcast, monitoring"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">Description</label>
                  <RichTextEditor
                    value={draft.prefill.bodyHtml ?? ''}
                    onChange={(v) => setPrefill({ bodyHtml: v || undefined })}
                    placeholder="What the requester would have written."
                  />
                </div>
                <div>
                  {/* The work note — the runbook, the checks to run. Never
                      sent to the requester, and tinted so that is obvious. */}
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Internal note <span className="font-normal">— your team only, never sent</span>
                  </label>
                  <RichTextEditor
                    value={draft.prefill.noteHtml ?? ''}
                    onChange={(v) => setPrefill({ noteHtml: v || undefined })}
                    placeholder="Runbook steps, what to check first…"
                    tinted
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button disabled={!dirty || !draft.name.trim() || save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">No templates yet.</p>
          <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-muted-foreground">
            Make one for the ticket your team raises most often, and it appears at the top of the agent's new
            ticket form.
          </p>
          <Button className="mt-4" size="sm" onClick={() => open()}>
            <Plus /> New template
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{t.name}</span>
                  {!t.isActive && (
                    <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                      Off
                    </Badge>
                  )}
                  {/* Usage, because "which of these does the team actually
                      use" is the question that decides what to delete. */}
                  {t.useCount > 0 && (
                    <span className="text-xs text-muted-foreground">
                      used {t.useCount} time{t.useCount === 1 ? '' : 's'}
                    </span>
                  )}
                </div>
                {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
              </div>
              <Button size="sm" variant="outline" onClick={() => open(t)}>
                Edit
              </Button>
              {/* Clone. His six real templates are near-identical variations
                  ("Create Phone Incident", "Create Walk-Up Incident"), which
                  is exactly how template sets get built — from each other. */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDraft({
                    id: '',
                    name: `${t.name} (copy)`,
                    description: t.description ?? '',
                    prefill: { ...t.prefill },
                  });
                  setEditId('new');
                }}
              >
                Clone
              </Button>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Delete ${t.name}`}
                onClick={async () => {
                  if (
                    await confirm({
                      title: `Delete “${t.name}”?`,
                      body: 'Tickets already made from it are unaffected.',
                      destructive: true,
                      confirmLabel: 'Delete',
                    })
                  )
                    remove.mutate(t.id);
                }}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
